import { AutoplayContext, MatchingUsersTypes, SearchSessionsMap, SeedData } from './types';
import { ActionType, createAction } from '@fullstory/redux-util/utils';
import { createGRPCAction, RequestActionType } from '@fullstory/redux-util';
import { optRefreshUserList } from '@fullstory/searchiecache';
import { SEARCHIE_REQUEST_BATCH_SIZE, skipCacheOpts, USERLIST_GRANULARIZATION } from './utils';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { SearchCacheKey } from '../searchMeta';
import { EventSearch } from '@fullstory/proto/fs/api/search';
import { AppDispatch, AppState, AppThunkAction, AppThunkContext } from '../../types';
import { getUsers, matchingUserTimeRangeCounts, welcomeStateFromAllTimeCounts } from './selectors';
import { GravatarThunks } from '../gravatars';
import { WatchHistoryRequestActions } from '../watchHistory';
import {
  GenerateSearchieResponseProto,
  QueryCacheOptions,
  SearchieMatchingUsersSettings,
  SearchieType,
  SearchServicePublic as svc,
  SearchSessionsResponse,
} from '@fullstory/proto/fs/services/alexa';
import { SearchSessionsRequestActions } from '../searchSessions';
import { Duration, Timestamp } from '@fullstory/proto/google/protobuf';
import jstz from 'jstz';
import { hashObject } from '@fullstory/util/hash';
import { alexaGrpcTimeoutHeader } from '../searchSessions/actions';
import { isRequestFailure } from '@fullstory/util/xhr';
import { UID } from '@fullstory/util/uid';

export const MatchingUsersActions = {
  fetchUsersLoading: (loading: boolean) =>
    createAction({
      type: MatchingUsersTypes.FetchUsersLoading,
      loading,
    }),
  fetchUsersStart: () =>
    createAction({
      type: MatchingUsersTypes.FetchUsersStart,
    }),
  fetchUsersSuccess: (
    data: GenerateSearchieResponseProto | null,
    segmentDefinition: SegmentDefinition,
  ) =>
    createAction({
      type: MatchingUsersTypes.FetchUsersSuccess,
      data,
      segmentDefinition,
    }),
  fetchUsersFailure: () =>
    createAction({
      type: MatchingUsersTypes.FetchUsersFailure,
    }),
  fetchUsersCacheUpdate: (
    data: GenerateSearchieResponseProto | null,
    loading: boolean,
    error: boolean,
  ) =>
    createAction({
      type: MatchingUsersTypes.FetchUsersCacheUpdate,
      data,
      loading,
      error,
      now: new Date(),
    }),
  fetchUsersCacheSwap: () =>
    createAction({
      type: MatchingUsersTypes.FetchUsersCacheSwap,
      now: new Date(),
    }),
  timeRangeCacheUpdate: (
    data: GenerateSearchieResponseProto | undefined,
    loading: boolean,
    error: boolean,
  ) =>
    createAction({
      type: MatchingUsersTypes.TimeRangeCacheUpdate,
      data,
      loading,
      error,
      now: new Date(),
    }),
  timeRangeCacheSwap: () =>
    createAction({
      type: MatchingUsersTypes.TimeRangeCacheSwap,
      now: new Date(),
    }),
  allTimeRangeCacheUpdate: (
    data: GenerateSearchieResponseProto | undefined,
    loading: boolean,
    error: boolean,
  ) =>
    createAction({
      type: MatchingUsersTypes.AllTimeRangeCacheUpdate,
      data,
      loading,
      error,
      now: new Date(),
    }),
  allTimeRangeCacheSwap: () =>
    createAction({
      type: MatchingUsersTypes.AllTimeRangeCacheSwap,
      now: new Date(),
    }),
  fetchSessionsStart: () =>
    createAction({
      type: MatchingUsersTypes.FetchSessionsStart,
    }),
  fetchSessionsSuccess: (data: SearchSessionsMap) =>
    createAction({
      type: MatchingUsersTypes.FetchSessionsSuccess,
      data,
    }),
  fetchSessionsFailure: () =>
    createAction({
      type: MatchingUsersTypes.FetchSessionsFailure,
    }),
  // Pass along the current matching user state to autoplay
  seedAutoplay: (data: SeedData) =>
    createAction({
      type: MatchingUsersTypes.SeedData,
      data,
    }),
  // This passes along all the info that Autoplay needs to fetch future batches
  // of sessions
  setAutoplayContext: (data: AutoplayContext) =>
    createAction({
      type: MatchingUsersTypes.SetAutoplayContext,
      data,
    }),
  // Resets the autoplay context back to the default state
  resetAutoplay: () =>
    createAction({
      type: MatchingUsersTypes.ResetAutoplay,
    }),
};

export type SearchSessionsPayload = EventSearch | SegmentDefinition[];
export type MatchingUsersActions =
  | ActionType<typeof MatchingUsersActions>
  | RequestActionType<typeof MatchingUsersRequestActions>;

export const MatchingUsersRequestActions = {
  fetchMatchingUsers: (
    segmentDefinition: SegmentDefinition,
    matchingUsersSettings: SearchieMatchingUsersSettings,
    orgId: string,
    followUp: boolean,
    refresh: boolean,
    page: number,
    searchCacheKey: SearchCacheKey,
    cacheOptionsParam?: QueryCacheOptions | null,
    uid?: UID,
  ) => {
    const cacheOptions =
      cacheOptionsParam ?? page > 0 ? skipCacheOpts : refresh ? optRefreshUserList : null;

    const handle = {
      type: SearchieType.SEARCHIE_TYPE_MATCHING_USERS,
      segmentDefinition,
      matchingUsersSettings,
    };

    return createGRPCAction<typeof svc.GenerateSearchie, GenerateSearchieResponseProto>({
      modify: (d: GenerateSearchieResponseProto) => d,
      cacheKey: (_: string) => `${hashObject(handle)}:${hashObject(searchCacheKey)}`,
      uid,
    })(
      {
        type: MatchingUsersTypes.FetchMatchingUsersStart,
        grpcFunction: svc.GenerateSearchie,
        body: {
          orgId,
          handle,
          timezone: jstz.determine().name(),
          now: null,
          granularization: Duration.fromMillis(USERLIST_GRANULARIZATION),
          cacheOptions,
          sampleOptions: null,
          followUp,
        },
        readWrite: 'write',
        settings: {
          batchOptions: {
            maxOutgoingRequests: SEARCHIE_REQUEST_BATCH_SIZE,
            id: hashObject(segmentDefinition).toString(),
          },
          headers: alexaGrpcTimeoutHeader,
        },
        force: refresh || page > 0,
      },
      MatchingUsersTypes.FetchMatchingUsersSuccess,
      MatchingUsersTypes.FetchMatchingUsersFailure,
    );
  },
  fetchTimeRange: (
    segmentDefinition: SegmentDefinition,
    searchCacheKey: SearchCacheKey,
    orgId: string,
    followUp: boolean,
    force?: boolean,
  ) => {
    const body = {
      orgId,
      handle: {
        type: SearchieType.SEARCHIE_TYPE_TIME_RANGE_COUNTS,
        segmentDefinition,
        timeRangeCountsSettings: { countMatching: true },
      },
      timezone: jstz.determine().name(),
      now: null,
      granularization: Duration.fromMillis(USERLIST_GRANULARIZATION),
      cacheOptions: null,
      sampleOptions: null,
      followUp,
    };
    return createGRPCAction<typeof svc.GenerateSearchie>({
      cacheKey: (url: string) => `${url}:${hashObject(body)}:${hashObject(searchCacheKey)}`,
    })(
      {
        type: MatchingUsersTypes.FetchTimeRangeStart,
        grpcFunction: svc.GenerateSearchie,
        body,
        settings: {
          batchOptions: {
            maxOutgoingRequests: SEARCHIE_REQUEST_BATCH_SIZE,
            id: hashObject(segmentDefinition).toString(),
          },
          headers: alexaGrpcTimeoutHeader,
        },
        force,
        readWrite: 'write',
      },
      MatchingUsersTypes.FetchTimeRangeSuccess,
      MatchingUsersTypes.FetchTimeRangeFailure,
    );
  },
  fetchAllTimeRange: (orgId: string, followUp: boolean) =>
    createGRPCAction<typeof svc.GenerateSearchie>()(
      {
        type: MatchingUsersTypes.FetchAllTimeRangeStart,
        grpcFunction: svc.GenerateSearchie,
        body: {
          orgId,
          handle: {
            type: SearchieType.SEARCHIE_TYPE_TIME_RANGE_COUNTS,
            segmentDefinition: {
              constraints: { timeRange: { startTime: '', endTime: '' }, completeSessions: false },
            } as SegmentDefinition,
          },
          timezone: jstz.determine().name(),
          now: null,
          granularization: Duration.fromMillis(USERLIST_GRANULARIZATION),
          cacheOptions: null,
          sampleOptions: null,
          followUp,
        },
        readWrite: 'write',
        settings: {
          batchOptions: {
            maxOutgoingRequests: SEARCHIE_REQUEST_BATCH_SIZE,
            id: MatchingUsersTypes.FetchAllTimeRangeStart,
          },
          headers: alexaGrpcTimeoutHeader,
        },
      },
      MatchingUsersTypes.FetchAllTimeRangeSuccess,
      MatchingUsersTypes.FetchAllTimeRangeFailure,
    ),
};

export type MatchingUsersThunkAction<R> = AppThunkAction<R, MatchingUsersActions>;

export const MatchingUsersAsyncActions = {
  fetch:
    (
      segmentDefinition: SegmentDefinition,
      searchCacheKey: SearchCacheKey,
      settings: SearchieMatchingUsersSettings,
      sessionSegmentDefinitions: SegmentDefinition[] | undefined = [],
      page: number,
      orgId: string,
      followUpReq: boolean,
      refresh: boolean,
      fetchGravatars: boolean,
    ): MatchingUsersThunkAction<Promise<void>> =>
    async (dispatch: AppDispatch, getState: () => AppState, context: AppThunkContext) => {
      dispatch(MatchingUsersActions.fetchUsersStart());
      dispatch(MatchingUsersActions.fetchUsersLoading(true));
      // keep track of the current request count, making sure that another batch of requests isn't about to be processed
      const requestCount = getState().matchingUsers.userFetchCount;
      try {
        let data = await dispatch(
          MatchingUsersRequestActions.fetchMatchingUsers(
            segmentDefinition,
            settings,
            orgId,
            followUpReq,
            refresh,
            page,
            searchCacheKey,
          ),
        );
        let matchingUsersTimeRange = matchingUserTimeRangeCounts(data);
        let timeRangeSearchieResponse: GenerateSearchieResponseProto | null = null;
        // if we can understand the time range counts from the matching users actions, don't fire any additional requests
        // otherwise, wait and fetch as much data is needed to get time range counts before considering this a successful fetch.
        if (matchingUsersTimeRange.shouldGenerateTimeRangeCounts) {
          try {
            timeRangeSearchieResponse = await dispatch(
              MatchingUsersRequestActions.fetchTimeRange(
                segmentDefinition,
                searchCacheKey,
                orgId,
                followUpReq,
              ),
            );
            // there are cases where either the matching users or the time range request will fail due to a 502 bad gateway, causing
            // one to recompute but not the other which can lead to a mismatch in the user count shown in the time range counts and
            // the matching users card. the workaround is to reissue both requests if the computed at timestamps are off by more than
            // the `ComputeIfOlderThan` value
            if (
              Math.abs(
                Timestamp.toMillis(timeRangeSearchieResponse.computedAt) -
                  matchingUsersTimeRange.countsComputedAt,
              ) > Duration.toMillis(timeRangeSearchieResponse?.cacheOptions?.computeIfOlderThan)
            ) {
              // force request by setting `refresh` to `true`
              data = await dispatch(
                MatchingUsersRequestActions.fetchMatchingUsers(
                  segmentDefinition,
                  settings,
                  orgId,
                  followUpReq,
                  true,
                  page,
                  searchCacheKey,
                ),
              );
              matchingUsersTimeRange = matchingUserTimeRangeCounts(data);
              timeRangeSearchieResponse = await dispatch(
                MatchingUsersRequestActions.fetchTimeRange(
                  segmentDefinition,
                  searchCacheKey,
                  orgId,
                  followUpReq,
                  true,
                ),
              );
            }
            if (getState().matchingUsers.userFetchCount === requestCount) {
              dispatch(
                MatchingUsersActions.timeRangeCacheUpdate(timeRangeSearchieResponse, false, false),
              );
            }
          } catch (e) {
            dispatch(MatchingUsersActions.timeRangeCacheUpdate(undefined, false, true));
          }
          // if welcome state still cannot be determined, fetch for all time range
          if (
            matchingUsersTimeRange.welcomeState === null &&
            welcomeStateFromAllTimeCounts(
              timeRangeSearchieResponse?.generatedSearchie?.timeRangeCountsData ?? null,
            ) === null
          ) {
            try {
              const allTimeRangeCountsRetry = await dispatch(
                MatchingUsersRequestActions.fetchAllTimeRange(orgId, followUpReq),
              );
              dispatch(
                MatchingUsersActions.allTimeRangeCacheUpdate(allTimeRangeCountsRetry, false, false),
              );
            } catch (e) {
              dispatch(MatchingUsersActions.allTimeRangeCacheUpdate(undefined, false, true));
            }
          }
        }

        // if the request count isn't equal, a new batch has started, so early exit and don't update with old data
        if (getState().matchingUsers.userFetchCount !== requestCount) {
          return;
        }

        // fetch user success MUST come first, otherwise state for user data may be out of date in the subsequent calls to fetch sessions,
        // since fetchSesssions reads back the users.
        dispatch(MatchingUsersActions.fetchUsersSuccess(data, segmentDefinition));
        dispatch(MatchingUsersActions.fetchUsersCacheUpdate(data, false, false));
        // additional segment definition is only present in the case of I&I
        // if present, session segment definition should only contain the additional filters
        // More info: https://github.com/cowpaths/mn/pull/13631#issuecomment-509720366
        const sessionPayload =
          settings.eventSearch ??
          (sessionSegmentDefinitions?.length ? sessionSegmentDefinitions : [segmentDefinition]);
        const sessionPromise = dispatch(
          MatchingUsersAsyncActions.fetchSessions(sessionPayload, context.orgId()),
        );
        const gravatarPromise = fetchGravatars
          ? dispatch(
              GravatarThunks.fetchGravatars(
                data?.generatedSearchie?.matchingUsersData?.users.map(u => u.email) ?? [],
              ),
            )
          : Promise.resolve();
        const watchHistoryPromise = dispatch(WatchHistoryRequestActions.fetchWatchHistory());
        await Promise.all([sessionPromise, gravatarPromise, watchHistoryPromise]);
        // if the request count isn't equal, a new batch has started, so early exit and don't update with old data
        if (getState().matchingUsers.userFetchCount !== requestCount) {
          return;
        }
        dispatch(MatchingUsersActions.fetchUsersLoading(false));
      } catch (e) {
        const {
          matchingUsers: {
            users: { data },
          },
        } = getState();
        if (isRequestFailure(e) && e.status === 0) {
          if (getState().matchingUsers.userFetchCount === requestCount) {
            dispatch(MatchingUsersActions.fetchUsersSuccess(data, segmentDefinition));
            dispatch(MatchingUsersActions.fetchUsersCacheUpdate(data, false, false));
          }
        } else {
          if (getState().matchingUsers.userFetchCount === requestCount) {
            dispatch(MatchingUsersActions.fetchUsersFailure());
            dispatch(MatchingUsersActions.fetchUsersCacheUpdate(data, false, true));
          }
          throw e;
        }
        dispatch(MatchingUsersActions.fetchUsersLoading(false));
      }
    },

  fetchSessions:
    (payload: SearchSessionsPayload, orgId: string): MatchingUsersThunkAction<Promise<void>> =>
    async (dispatch: AppDispatch, getState: () => AppState): Promise<void> => {
      const state = getState();
      const users = getUsers(state);
      dispatch(MatchingUsersActions.fetchSessionsStart());
      try {
        const responses = await Promise.all(
          users
            .filter(u => u.fetchClips)
            .map(async ({ indvId }) => {
              const id = indvId as string;
              const data = await dispatch(
                SearchSessionsRequestActions.searchSessions(payload, id, orgId, {
                  maxOutgoingRequests: 3,
                  id: 'clipList',
                }),
              );
              return { id, data };
            }),
        );
        const data = responses.reduce<{ [id: string]: SearchSessionsResponse }>(
          (acc, { id, data }) => ({ ...acc, [id]: data }),
          {},
        );

        dispatch(MatchingUsersActions.fetchSessionsSuccess(data));
      } catch (e) {
        dispatch(MatchingUsersActions.fetchSessionsFailure());
      }
    },
};
